/**
 * Auth portal (auth-qa) refresh token service.
 * Calls POST /v1/auth/refresh with credentials so the server can read the
 * refresh token from HTTP-only cookies and set new accessToken/idToken via Set-Cookie.
 *
 * CSRF: GET /v1/auth/csrfToken at session start sets XSRF-TOKEN; reuse that cookie for later
 * POST /v1/auth/refresh calls (same value in X-CSRF-Token header). We only call GET again when
 * the cookie is missing (first use, cleared, new tab without cookie) or after a 403 CSRF failure.
 *
 * Calling pattern mirrors CognitoCustomChallengeService: single refresh entry point
 * used by axios interceptor (on 401 / invalid session) and optionally by a timer.
 */

import { getAuthPortalBaseUrl } from './authPortal';

const REFRESH_PATH = '/v1/auth/refresh';
const CSRF_TOKEN_PATH = '/v1/auth/csrfToken';

/**
 * Full URL for the auth portal refresh endpoint.
 * @returns {string}
 */
export function getRefreshUrl() {
  const base = getAuthPortalBaseUrl().replace(/\/$/, '');
  return `${base}${REFRESH_PATH}`;
}

/**
 * Full URL for the auth portal CSRF token endpoint.
 * @returns {string}
 */
function getCsrfTokenUrl() {
  const base = getAuthPortalBaseUrl().replace(/\/$/, '');
  return `${base}${CSRF_TOKEN_PATH}`;
}

/**
 * Read a cookie value by name from document.cookie (same-origin only).
 * @param {string} name - Cookie name
 * @returns {string|null}
 */
function getCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name.replace(/[-.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Fetch CSRF token from auth portal. Must be called at the start of each session.
 * Server sets XSRF-TOKEN cookie; if the API returns the token in the response body we use that
 * (required for cross-origin), otherwise we fall back to reading the cookie (same-origin/proxy).
 *
 * @returns {Promise<string>} The CSRF token to send as X-CSRF-Token header
 */
async function fetchCsrfToken() {
  const url = getCsrfTokenUrl();
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Accept': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Auth portal CSRF token request failed: ${response.status}`);
  }

  // Prefer token in response body (enables cross-origin; server may echo the cookie value)
  try {
    const json = await response.json();
    const token = json?.token ?? json?.csrfToken ?? json?.xsrfToken ?? json?.csrf ?? json?.XSRF_TOKEN;
    if (token && typeof token === 'string') return token;
  } catch (_) {
    // non-JSON or empty body
  }

  // Fallback: read cookie (works when same-origin or proxy sets cookie on our origin)
  const fromCookie = getCookie('XSRF-TOKEN');
  if (fromCookie) return fromCookie;

  throw new Error('Auth portal did not return a CSRF token (check response body or XSRF-TOKEN cookie)');
}

/**
 * CSRF value for the X-CSRF-Token header: reuse readable XSRF-TOKEN cookie when present so
 * periodic refresh does not hit GET /csrfToken every time. If the cookie is missing or
 * forceRefresh is true, calls GET /v1/auth/csrfToken.
 *
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<string>}
 */
async function getCsrfTokenValue({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    const fromCookie = getCookie('XSRF-TOKEN');
    if (fromCookie) return fromCookie;
  }
  return fetchCsrfToken();
}

/**
 * Explicit session bootstrap: GET /v1/auth/csrfToken so the server sets XSRF-TOKEN (optional if
 * the first refresh will run soon — refreshAccessToken will call GET when the cookie is absent).
 *
 * @returns {Promise<string>} Token for X-CSRF-Token on subsequent state-changing requests
 */
export async function primeAuthPortalCsrfToken() {
  return fetchCsrfToken();
}

/**
 * Refresh access token using the auth portal API.
 * Uses existing XSRF-TOKEN when readable; otherwise GET /v1/auth/csrfToken once, then POST
 * /v1/auth/refresh with X-CSRF-Token. Retries once with a fresh CSRF GET after 403.
 * On success (200), the server sets new accessToken and idToken via Set-Cookie.
 *
 * @returns {Promise<{ success: boolean }>} Resolves with { success: true } on 200
 * @throws On non-2xx or network error (e.g. 401 "JWT Bearer token not supplied, invalid, or expired")
 */
export async function refreshAccessToken() {
  const url = getRefreshUrl();
  let forceCsrf = false;

  for (let attempt = 0; attempt < 2; attempt++) {
    const csrfToken = await getCsrfTokenValue({ forceRefresh: forceCsrf });
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
    });

    if (response.ok) {
      let data = { success: true };
      try {
        const json = await response.json();
        if (json && typeof json === 'object') data = { ...data, ...json };
      } catch (_) {
        // 200 with empty or non-JSON body is still success (tokens set via Set-Cookie)
      }
      return data;
    }

    if (response.status === 403 && attempt === 0) {
      forceCsrf = true;
      continue;
    }

    let message = `Auth portal refresh failed: ${response.status}`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch (_) {
      // ignore
    }
    const error = new Error(message);
    error.status = response.status;
    error.response = response;
    throw error;
  }
}

export default {
  getRefreshUrl,
  primeAuthPortalCsrfToken,
  refreshAccessToken,
};
